import { useState, useEffect } from 'react'
import { useBranch } from '../contexts/BranchContext'
import { washService, expenseService } from '../services/firebase'
import {
  TrendingUp,
  Calendar,
  DollarSign,
  Car,
  BarChart3,
  RefreshCw,
  Users,
  PieChart,
  Star,
  FileText,
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import PageHeader from '../components/PageHeader'
import StatTile from '../components/StatTile'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'

function washDateValue(wash) {
  if (!wash?.createdAt) return null
  return wash.createdAt.toDate ? wash.createdAt.toDate() : new Date(wash.createdAt)
}

export default function ReportsPage() {
  const { currentBranch } = useBranch()
  const [loading, setLoading] = useState(true)
  const [reportData, setReportData] = useState({
    revenue: 0,
    expenses: 0,
    washes: 0,
    profit: 0,
    uniqueCustomers: 0,
    averageOrderValue: 0,
    newCustomers: 0,
    returningCustomers: 0,
    retentionRate: 0,
    revenueData: null,
    serviceDistribution: null,
    topServices: null,
  })
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  })

  useEffect(() => {
    loadReportData()
  }, [currentBranch, dateRange.start, dateRange.end])

  const loadReportData = async () => {
    if (!currentBranch) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const startDate = new Date(dateRange.start)
      const endDate = new Date(dateRange.end)
      endDate.setHours(23, 59, 59, 999)

      const [washes, expenses] = await Promise.all([
        washService.getWashesByBranch(currentBranch.id),
        expenseService.getExpensesByBranch(currentBranch.id, startDate, endDate),
      ])

      const filteredWashes = washes.filter((wash) => {
        const d = washDateValue(wash)
        if (!d) return false
        return d >= startDate && d <= endDate
      })

      const totalRevenue = filteredWashes.reduce((sum, w) => sum + (w.amount || 0), 0)
      const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0)
      const profit = totalRevenue - totalExpenses
      const ids = new Set(filteredWashes.map((w) => w.customerId).filter(Boolean))
      const avg = filteredWashes.length ? totalRevenue / filteredWashes.length : 0

      setReportData({
        revenue: totalRevenue,
        expenses: totalExpenses,
        washes: filteredWashes.length,
        profit,
        uniqueCustomers: ids.size,
        averageOrderValue: avg,
        newCustomers: 0,
        returningCustomers: 0,
        retentionRate: 0,
        revenueData: null,
        serviceDistribution: null,
        topServices: null,
      })
    } catch (error) {
      console.error('Error loading report data:', error)
      toast.error('Failed to load report data')
    } finally {
      setLoading(false)
    }
  }

  const exportReport = () => {
    const reportContent = `
TrendSetter - Business report
Branch: ${currentBranch?.name || 'N/A'}
Period: ${dateRange.start} to ${dateRange.end}
Generated: ${new Date().toLocaleDateString()}

=== Summary ===
Revenue: R${reportData.revenue.toFixed(2)}
Expenses: R${reportData.expenses.toFixed(2)}
Net: R${reportData.profit.toFixed(2)}
Washes: ${reportData.washes}
Unique customers: ${reportData.uniqueCustomers}
Avg order: R${reportData.averageOrderValue.toFixed(2)}
`
    const blob = new Blob([reportContent], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `trendsetter-report-${dateRange.start}-to-${dateRange.end}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success('Report downloaded')
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-8 pb-8">
      <PageHeader
        eyebrow="Analytics"
        title="Reports"
        description={`${currentBranch?.name || 'Branch'} - financial and volume metrics for the selected period.`}
      >
        <Button variant="outline" size="sm" className="gap-2" onClick={() => loadReportData()}>
          <RefreshCw className="size-4" />
          Refresh
        </Button>
      </PageHeader>

      <Card className="border-border/80 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-medium">Date range</CardTitle>
          <CardDescription>Filter washes and expenses for this window</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-end">
          <div className="space-y-2">
            <Label htmlFor="start">From</Label>
            <Input
              id="start"
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange((d) => ({ ...d, start: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="end">To</Label>
            <Input
              id="end"
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange((d) => ({ ...d, end: e.target.value }))}
            />
          </div>
          <Button type="button" onClick={() => loadReportData()} className="gap-2">
            <Calendar className="size-4" />
            Apply
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Revenue"
          value={`R${reportData.revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          sub="in period"
          icon={DollarSign}
          tone="success"
        />
        <StatTile
          label="Washes"
          value={String(reportData.washes)}
          sub="completed in range"
          icon={Car}
          tone="info"
        />
        <StatTile
          label="Unique customers"
          value={String(reportData.uniqueCustomers)}
          sub="distinct IDs"
          icon={Users}
          tone="violet"
        />
        <StatTile
          label="Avg. order"
          value={`R${reportData.averageOrderValue.toFixed(2)}`}
          sub="per wash"
          icon={TrendingUp}
          tone="warning"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="md:col-span-2 border-border/80 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <BarChart3 className="size-5" />
              </div>
              <div>
                <CardTitle className="text-lg">P&amp;L snapshot</CardTitle>
                <CardDescription>Revenue minus recorded expenses</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <p className="text-muted-foreground">Expenses</p>
                <p className="text-lg font-semibold tabular-nums">
                  R{reportData.expenses.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Net</p>
                <p
                  className={cn(
                    'text-lg font-semibold tabular-nums',
                    reportData.profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'
                  )}
                >
                  R{reportData.profit.toFixed(2)}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Expense ratio</span>
                <span>
                  {reportData.revenue > 0
                    ? `${((reportData.expenses / reportData.revenue) * 100).toFixed(1)}%`
                    : 'N/A'}
                </span>
              </div>
              <Progress
                value={
                  reportData.revenue > 0
                    ? Math.min(100, (reportData.expenses / reportData.revenue) * 100)
                    : 0
                }
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-violet-500/15 p-2 text-violet-700 dark:text-violet-300">
                <PieChart className="size-5" />
              </div>
              <CardTitle className="text-lg">Export</CardTitle>
            </div>
            <CardDescription>Plain-text summary for spreadsheets</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="secondary" className="w-full gap-2" onClick={exportReport}>
              <FileText className="size-4" />
              Download .txt report
            </Button>
            <p className="text-xs text-muted-foreground mt-3">
              PDF/Excel exports can be added when connected to reporting APIs.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-dashed bg-muted/20 shadow-none">
        <CardContent className="flex flex-col items-center gap-2 py-10 text-center sm:flex-row sm:justify-center">
          <Star className="size-8 text-muted-foreground opacity-60" />
          <div className="text-sm text-muted-foreground max-w-md">
            Charts for revenue trend and service mix will populate when historical aggregates are
            wired. Core numbers above reflect your wash and expense data for the selected range.
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
